import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Icons } from '../components/shared/Icons';
import GradientText from '../components/shared/GradientText';
import GlowButton from '../components/shared/GlowButton';
import ScrollReveal from '../components/shared/ScrollReveal';
import AIChatbot from '../components/support/AIChatbot';
import './DraftPages.css';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xnjdpgkd';

const SUPPORT_OPTIONS = [
  {
    icon: 'brain',
    title: 'AI Assistant',
    description: 'Get instant answers from our AI-powered support assistant, available 24/7.',
    action: 'chat',
    color: 'drone',
  },
  {
    icon: 'server',
    title: 'Email Support',
    description: 'Send us a detailed message and we\'ll respond within 24 hours.',
    action: 'mailto:support@ai4solar.org',
    color: 'solar',
  },
  {
    icon: 'smartphone',
    title: 'Phone Support',
    description: 'Speak directly with our support team during business hours.',
    action: 'tel:+1-888-SOLAR-99',
    color: 'energy',
  },
  {
    icon: 'layers',
    title: 'Knowledge Base',
    description: 'Browse our extensive documentation and tutorials.',
    action: '/docs',
    color: 'satellite',
  },
];

const FAQ_ITEMS = [
  {
    question: 'How do I connect my solar inverter to AI4Solar?',
    answer: 'AI4Solar supports most major inverter brands including Fronius, SolarEdge, Enphase, and more. Navigate to Settings > Devices > Add New Device, then select your inverter manufacturer and follow the guided setup process. For most inverters, you\'ll need your device\'s IP address or serial number.',
  },
  {
    question: 'Why is my production data not showing?',
    answer: 'If your production data is missing, first check that your inverter is online and connected to your network. Then verify the connection status in AI4Solar under Settings > Devices. Common issues include network changes, inverter firmware updates, or API credential expiration. Our AI assistant can help diagnose specific issues.',
  },
  {
    question: 'How accurate are the energy forecasts?',
    answer: 'Our ML-powered forecasts typically achieve 90-95% accuracy for day-ahead predictions. Accuracy depends on factors like your location, system configuration, and local weather patterns. The system continuously learns from your actual production data to improve predictions over time.',
  },
  {
    question: 'Can I monitor multiple solar installations?',
    answer: 'Yes! AI4Solar supports fleet management for residential installers and commercial operators. You can monitor unlimited sites from a single dashboard, with customizable alerts and reporting for each location. Contact our sales team for volume pricing.',
  },
  {
    question: 'What API rate limits apply to my account?',
    answer: 'Rate limits vary by plan: Starter (100 req/min), Professional (500 req/min), and Enterprise (custom). WebSocket connections are unlimited on all plans. You can monitor your current usage in the API dashboard. If you need higher limits, contact us to discuss your requirements.',
  },
  {
    question: 'How do I export my historical data?',
    answer: 'Go to Analytics > Export Data. You can export data in CSV, JSON, or Excel format for any time range. For large exports or automated data pulls, we recommend using our API\'s batch export endpoint. Enterprise customers have access to direct database replication.',
  },
  {
    question: 'Is my data secure?',
    answer: 'Absolutely. We use bank-level encryption (AES-256) for data at rest and TLS 1.3 for data in transit. Our infrastructure is hosted on SOC 2 Type II certified cloud providers. We never share your data with third parties, and you can request complete data deletion at any time.',
  },
];

const ISSUE_CATEGORIES = [
  'Technical Issue',
  'Billing Question',
  'Account Access',
  'API & Integration',
  'Feature Request',
  'Other',
];

export default function Support() {
  const [chatOpen, setChatOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
    priority: 'normal',
  });
  const [formStatus, setFormStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: `[Support Ticket] ${formData.category}: ${formData.subject}`,
          _replyto: formData.email,
          form_type: 'support_ticket',
          name: formData.name,
          email: formData.email,
          category: formData.category,
          priority: formData.priority,
          subject: formData.subject,
          message: formData.message,
        })
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({
          name: '',
          email: '',
          category: '',
          subject: '',
          message: '',
          priority: 'normal',
        });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleSupportAction = (action) => {
    if (action === 'chat') {
      setChatOpen(true);
    } else if (action.startsWith('mailto:') || action.startsWith('tel:')) {
      window.location.href = action;
    }
  };

  const IconComponent = ({ name, size = 24 }) => {
    const Icon = Icons[name];
    return Icon ? <Icon size={size} /> : null;
  };

  return (
    <div className="draft-page support-page">
      {/* Hero Section */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">24/7 Support</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              <GradientText gradient="solar">We're Here</GradientText> to Help
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              Get the support you need, when you need it. Our team and AI assistant
              are ready to help you get the most out of AI4Solar.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <div className="support-hero-actions">
              <GlowButton variant="primary" onClick={() => setChatOpen(true)}>
                <Icons.brain size={18} />
                Chat with AI
              </GlowButton>
              <GlowButton variant="outline" href="#contact-form">
                Submit a Ticket
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Support Options */}
      <section className="support-section">
        <div className="container">
          <ScrollReveal>
            <div className="section-header">
              <h2>How Can We Help?</h2>
              <p>Choose the support channel that works best for you</p>
            </div>
          </ScrollReveal>
          <div className="support-options-grid">
            {SUPPORT_OPTIONS.map((option, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <motion.div
                  className={`support-option-card support-option-${option.color}`}
                  whileHover={{ y: -5 }}
                  onClick={() => option.action === 'chat'
                    ? handleSupportAction(option.action)
                    : null
                  }
                  style={{ cursor: option.action === 'chat' ? 'pointer' : 'default' }}
                >
                  <div className={`support-option-icon support-icon-${option.color}`}>
                    <IconComponent name={option.icon} size={28} />
                  </div>
                  <h3>{option.title}</h3>
                  <p>{option.description}</p>
                  {option.action === 'chat' ? (
                    <button className="support-option-link">
                      Start Chat <Icons.arrowRight size={16} />
                    </button>
                  ) : option.action.startsWith('/') ? (
                    <Link to={option.action} className="support-option-link">
                      Browse Docs <Icons.arrowRight size={16} />
                    </Link>
                  ) : (
                    <a
                      href={option.action}
                      className="support-option-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {option.action.startsWith('mailto:') ? 'Send Email' : 'Call Now'}{' '}
                      <Icons.arrowRight size={16} />
                    </a>
                  )}
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="support-section support-section-dark">
        <div className="container">
          <ScrollReveal>
            <div className="section-header">
              <h2>Frequently Asked Questions</h2>
              <p>Quick answers to common questions</p>
            </div>
          </ScrollReveal>
          <div className="faq-list">
            {FAQ_ITEMS.map((faq, index) => (
              <ScrollReveal key={index} delay={index * 0.05}>
                <div
                  className={`faq-item ${openFaq === index ? 'faq-open' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => toggleFaq(index)}
                    aria-expanded={openFaq === index}
                  >
                    <span>{faq.question}</span>
                    <motion.span
                      className="faq-icon"
                      animate={{ rotate: openFaq === index ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Icons.chevronDown size={20} />
                    </motion.span>
                  </button>
                  <motion.div
                    className="faq-answer"
                    initial={false}
                    animate={{
                      height: openFaq === index ? 'auto' : 0,
                      opacity: openFaq === index ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <p>{faq.answer}</p>
                  </motion.div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={0.3}>
            <div className="faq-more">
              <p>Can't find what you're looking for?</p>
              <GlowButton variant="secondary" onClick={() => setChatOpen(true)}>
                Ask our AI Assistant
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="support-section" id="contact-form">
        <div className="container">
          <div className="contact-form-wrapper">
            <ScrollReveal>
              <div className="contact-form-header">
                <h2>Submit a Support Ticket</h2>
                <p>
                  For complex issues or detailed inquiries, submit a ticket and our
                  team will get back to you within 24 hours.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <form className="support-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">Issue Category</label>
                    <select
                      id="category"
                      name="category"
                      className="form-select"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a category</option>
                      {ISSUE_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="priority">Priority</label>
                    <select
                      id="priority"
                      name="priority"
                      className="form-select"
                      value={formData.priority}
                      onChange={handleInputChange}
                    >
                      <option value="low">Low - General question</option>
                      <option value="normal">Normal - Issue but can work around</option>
                      <option value="high">High - Significant impact</option>
                      <option value="urgent">Urgent - System down</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="form-input"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Brief description of your issue"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-textarea"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, and what you've already tried."
                    rows={6}
                    required
                  />
                </div>
                <div className="form-actions">
                  <GlowButton
                    type="submit"
                    variant="primary"
                    disabled={formStatus === 'sending'}
                  >
                    {formStatus === 'sending' ? (
                      <>Submitting...</>
                    ) : formStatus === 'success' ? (
                      <>
                        <Icons.check size={18} />
                        Ticket Submitted!
                      </>
                    ) : formStatus === 'error' ? (
                      <>Try Again</>
                    ) : (
                      <>Submit Ticket</>
                    )}
                  </GlowButton>
                  {formStatus === 'success' && (
                    <p className="form-success">
                      We've received your ticket and will respond within 24 hours.
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="form-error">
                      Something went wrong. Please try again or email us at support@ai4solar.org
                    </p>
                  )}
                </div>
              </form>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal>
            <div className="draft-cta-content">
              <h2>Need Enterprise Support?</h2>
              <p>
                Get dedicated support, custom SLAs, and a named account manager
                for your organization.
              </p>
              <div className="cta-buttons">
                <GlowButton variant="primary" href="/contact">
                  Contact Sales
                </GlowButton>
                <GlowButton variant="outline" href="/pricing">
                  View Plans
                </GlowButton>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* AI Chatbot Widget */}
      <AIChatbot isOpen={chatOpen} onToggle={setChatOpen} />
    </div>
  );
}
